import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tv, Film, Clapperboard, Radio, Settings, Loader2, ChevronRight } from "lucide-react";
import axios from "axios";
import { brand, brandId } from "../brand.config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Shared data ─────────────────────────────────────────────────── */
const MENU_ITEMS = [
  { id: "live",    icon: Tv,          label: "Live TV",  desc: "Watch live channels",   color: "#F59E0B" },
  { id: "movies",  icon: Film,        label: "Movies",   desc: "Browse VOD library",    color: "#3B82F6" },
  { id: "series",  icon: Clapperboard,label: "Series",   desc: "TV shows & seasons",    color: "#8B5CF6" },
  { id: "radio",   icon: Radio,       label: "Radio",    desc: "Listen to stations",    color: "#EC4899" },
];

/* ══ ArcPlayer — slim list view ════════════════════════════════════ */
const ArcMenu = ({ onNavigate }) => (
  <div className="w-full max-w-xl mx-auto space-y-2 px-4">
    {MENU_ITEMS.map(({ id, icon: Icon, label, desc, color }) => (
      <button
        key={id}
        onClick={() => onNavigate(id)}
        data-testid={`menu-${id}`}
        className="group w-full flex items-center gap-4 px-5 py-4 rounded-lg transition-all duration-150"
        style={{
          background: "rgba(0,191,255,0.04)",
          border: `1px solid rgba(0,191,255,0.12)`,
          borderLeft: `3px solid ${brand.accent}`,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = "rgba(0,191,255,0.09)";
          e.currentTarget.style.border = `1px solid rgba(0,191,255,0.35)`;
          e.currentTarget.style.borderLeft = `3px solid ${brand.accent}`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = "rgba(0,191,255,0.04)";
          e.currentTarget.style.border = `1px solid rgba(0,191,255,0.12)`;
          e.currentTarget.style.borderLeft = `3px solid ${brand.accent}`;
        }}
      >
        {/* Icon */}
        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
          style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>

        {/* Text */}
        <div className="flex-1 text-left">
          <p className="text-sm font-semibold text-white tracking-wide">{label}</p>
          <p className="text-xs text-white/40 mt-0.5">{desc}</p>
        </div>

        {/* Arrow */}
        <ChevronRight className="w-4 h-4 text-white/25 group-hover:text-white/60 group-hover:translate-x-0.5 transition-all" />
      </button>
    ))}
  </div>
);

/* ══ PulsePlayer — Netflix poster grid ═════════════════════════════ */
const PulseMenu = ({ onNavigate }) => (
  <div className="w-full max-w-2xl mx-auto px-4">
    <div className="grid grid-cols-2 gap-4">
      {MENU_ITEMS.map(({ id, icon: Icon, label, desc, color }) => (
        <button
          key={id}
          onClick={() => onNavigate(id)}
          data-testid={`menu-${id}`}
          className="group relative aspect-video rounded-xl overflow-hidden transition-all duration-200"
          style={{
            background: `linear-gradient(135deg, ${color}22 0%, rgba(0,0,0,0.6) 100%)`,
            border: "1px solid rgba(255,255,255,0.06)",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = "scale(1.03)";
            e.currentTarget.style.border = `1px solid ${color}60`;
            e.currentTarget.style.boxShadow = `0 0 28px ${color}30`;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.border = "1px solid rgba(255,255,255,0.06)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          {/* Background glow blob */}
          <div className="absolute inset-0 opacity-20"
            style={{ background: `radial-gradient(circle at 30% 40%, ${color} 0%, transparent 65%)` }} />

          {/* Icon */}
          <div className="absolute top-4 left-4">
            <Icon className="w-7 h-7" style={{ color }} />
          </div>

          {/* Bottom label */}
          <div className="absolute bottom-0 left-0 right-0 p-4"
            style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.75) 0%, transparent 100%)" }}>
            <p className="text-sm font-semibold text-white">{label}</p>
            <p className="text-[10px] text-white/45 mt-0.5">{desc}</p>
          </div>
        </button>
      ))}
    </div>
  </div>
);

/* ══ NetTV — original card grid (unchanged) ════════════════════════ */
const NettvMenu = ({ onNavigate }) => {
  const cards = [
    { id: "live",    icon: Tv,          label: "LIVE TV",  desc: "Watch live channels",  gradient: "from-yellow-500 to-amber-600" },
    { id: "movies",  icon: Film,        label: "MOVIES",   desc: "Browse VOD library",   gradient: "from-blue-500 to-indigo-600" },
    { id: "series",  icon: Clapperboard,label: "SERIES",   desc: "TV shows & seasons",   gradient: "from-purple-500 to-violet-600" },
    { id: "radio",   icon: Radio,       label: "RADIO",    desc: "Listen to stations",   gradient: "from-pink-500 to-rose-600" },
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
      {cards.map(item => (
        <button
          key={item.id}
          onClick={() => onNavigate(item.id)}
          className="group flex flex-col items-center gap-2 sm:gap-3 p-4 sm:p-6 rounded-xl transition-all hover:scale-105 bg-white/5 border border-white/10 hover:border-white/20 w-28 sm:w-36"
          data-testid={`menu-${item.id}`}
        >
          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center bg-gradient-to-br ${item.gradient} shadow-lg`}>
            <item.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <span className="text-xs sm:text-sm font-medium">{item.label}</span>
          <span className="text-[9px] sm:text-[10px] text-white/40">{item.desc}</span>
        </button>
      ))}
    </div>
  );
};

/* ══ Page shell ═════════════════════════════════════════════════════ */
const MainMenuPage = () => {
  const navigate = useNavigate();
  const [deviceKey,      setDeviceKey]      = useState("");
  const [deviceStatus,   setDeviceStatus]   = useState(null);
  const [playlists,      setPlaylists]      = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [currentTime,    setCurrentTime]    = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const checkDevice = async () => {
      const storedKey = localStorage.getItem("deviceKey");
      if (!storedKey) { navigate("/activate"); return; }
      try {
        const { data } = await axios.get(`${API}/device/status/${storedKey}`);
        setDeviceKey(storedKey);
        setDeviceStatus(data);
        if (data.status === "expired" || data.status === "pending") { navigate("/activate"); return; }
        const { data: pls } = await axios.get(`${API}/playlist/${storedKey}`);
        setPlaylists(pls);
        if (pls.length > 0) setSelectedPlaylist(pls[0]);
      } catch { navigate("/activate"); }
      finally  { setLoading(false); }
    };
    checkDevice();
  }, [navigate]);

  const handleNavigate = (type) => {
    if (selectedPlaylist) localStorage.setItem("selectedPlaylistId", selectedPlaylist.id);
    navigate(`/player?type=${type}`);
  };

  const fmt = (d) => d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
    + " · " + d.toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short" });

  const isArc   = brandId === "arcplayer";
  const isPulse = brandId === "pulseplayer";
  const logoBase = process.env.PUBLIC_URL || "";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: brand.bg }}>
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3" style={{ color: brand.accent }} />
          <p className="text-white/60 text-sm">{brand.name}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: brand.bg }} data-testid="main-menu">

      {/* ── Header ──────────────────────────────────────────────── */}
      <header
        className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4"
        style={{
          background: "rgba(0,0,0,0.35)",
          borderBottom: `1px solid ${brand.accent}18`,
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="flex items-center gap-2">
          <img
            src={`${logoBase}/${brand.logoNav}`}
            alt={brand.logoAlt}
            className="h-7 sm:h-9 object-contain"
            onError={e => { e.target.style.display = "none"; }}
          />
          {brand.showNameBesideLogo && (
            <span className="font-bold text-sm hidden sm:block" style={{ color: brand.accent }}>
              {brand.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-white/40 hidden sm:block text-xs">{fmt(currentTime)}</span>
          <button
            onClick={() => navigate("/settings")}
            className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline text-xs">Settings</span>
          </button>
        </div>
      </header>

      {/* ── Main ────────────────────────────────────────────────── */}
      <main className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] px-4 py-8 gap-8">

        {/* Brand wordmark / logo */}
        <div className="text-center">
          <img
            src={`${logoBase}/${brand.logoHero}`}
            alt={brand.logoAlt}
            className={`${brand.heroLogoClass} object-contain mx-auto`}
            onError={e => { e.target.style.display = "none"; }}
          />
        </div>

        {/* Content grid — brand-specific layout */}
        {isArc   && <ArcMenu   onNavigate={handleNavigate} />}
        {isPulse && <PulseMenu onNavigate={handleNavigate} />}
        {!isArc && !isPulse && <NettvMenu onNavigate={handleNavigate} />}

        {/* Playlist selector */}
        {playlists.length > 1 && (
          <select
            value={selectedPlaylist?.id || ""}
            onChange={e => {
              const p = playlists.find(x => x.id === e.target.value);
              setSelectedPlaylist(p);
              localStorage.setItem("selectedPlaylistId", p?.id);
            }}
            className="px-4 py-2 rounded-lg text-white text-sm"
            style={{
              background: "rgba(0,0,0,0.5)",
              border: `1px solid ${brand.accent}30`,
              outline: "none",
            }}
            data-testid="playlist-selector"
          >
            {playlists.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </main>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer
        className="fixed bottom-0 left-0 right-0 px-5 py-3 flex items-center justify-between text-xs"
        style={{
          background: "rgba(0,0,0,0.6)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="flex items-center gap-3 text-white/50">
          <span>Device: <span className="font-mono" style={{ color: brand.accent }}>{deviceKey}</span></span>
          <span className="text-white/20">|</span>
          <span>
            {deviceStatus?.is_paid ? "LIFETIME" : deviceStatus?.trial_end
              ? new Date(deviceStatus.trial_end).toLocaleDateString()
              : "N/A"}
          </span>
        </div>
        <button
          onClick={() => navigate("/activate")}
          className="text-white/40 hover:text-white transition-colors"
          data-testid="back-btn"
        >
          Back
        </button>
      </footer>
    </div>
  );
};

export default MainMenuPage;
